import * as React from "react";

// What this app is CALLED and how it looks once installed: the manifest, the iPhone home-screen
// icon and title, and the name in the header.
//
// One app, one identity. It used to depend on the address it was opened from: index.html linked
// "./manifest.webmanifest", which at webmail.<domain>/ozpanel/ is the generic static manifest
// ("ozpanel Mailbox", one SVG icon, scope /ozpanel/) and at /app or / is the hub's per-domain one
// (the domain's own name and logo, PNG icons, scope /). So the same customer installed a
// differently named app depending on which link they followed — one of them naming the platform.
//
// Now: wherever the webmail hub serves this domain's own manifest (GET /manifest.webmanifest at the
// origin root, rendered per Host by the panel), that is THE manifest, and its name and icon are used
// for the iPhone title, the home-screen icon and the header. Where it does not exist (the panel's
// own host), the console's built-in manifest is used — named plainly "Mailbox".
// Measured from the response, not guessed from the hostname.

export interface AppIdentity {
  name: string;
  shortName: string;
}

const DEFAULT: AppIdentity = { name: "Mailbox", shortName: "Mailbox" };
let current: AppIdentity = DEFAULT;
const listeners = new Set<() => void>();

function setHeadLink(rel: string, href: string, extra?: Record<string, string>): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
  for (const [k, v] of Object.entries(extra ?? {})) el.setAttribute(k, v);
}

function setMeta(name: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.name = name;
    document.head.appendChild(el);
  }
  el.content = content;
}

interface Manifest {
  name?: string;
  short_name?: string;
  theme_color?: string;
  icons?: { src?: string; sizes?: string; type?: string }[];
}

export function applyAppIdentity(base: string): void {
  // The built-in manifest first, so an install offered before the check finishes is still valid.
  setHeadLink("manifest", base + "/manifest.webmanifest");
  // Re-pointed after the address was canonicalised: "./icon.svg" parsed at /app meant /icon.svg.
  setHeadLink("icon", base + "/icon.svg", { type: "image/svg+xml" });
  setMeta("apple-mobile-web-app-title", DEFAULT.shortName);
  setMeta("mobile-web-app-capable", "yes");
  setMeta("apple-mobile-web-app-capable", "yes");

  void fetch("/manifest.webmanifest", { credentials: "same-origin", cache: "no-store" })
    .then(async (r) => {
      const type = r.headers.get("content-type") ?? "";
      if (!r.ok || !/manifest|json/.test(type)) return null;
      return (await r.json()) as Manifest;
    })
    .then((m) => {
      if (!m || !m.name) return;
      setHeadLink("manifest", "/manifest.webmanifest");
      const short = m.short_name || m.name;
      setMeta("apple-mobile-web-app-title", short);
      if (m.theme_color) setMeta("theme-color", m.theme_color);
      // iOS takes its home-screen icon from apple-touch-icon, not from the manifest, on every
      // version that matters here — without one it uses a screenshot of the page.
      const png = (m.icons ?? []).find((i) => i.src && /192/.test(i.sizes ?? "") && !/svg/.test(i.type ?? ""));
      if (png?.src) setHeadLink("apple-touch-icon", png.src);
      document.title = short;
      current = { name: m.name, shortName: short };
      listeners.forEach((l) => l());
    })
    .catch(() => {
      /* No per-domain manifest here: the built-in one stays. */
    });
}

export function useAppIdentity(): AppIdentity {
  return React.useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => current,
    () => DEFAULT,
  );
}
